import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { DetectedBrandTermSkeleton, DetectedBrandTerm } from 'src/components/DetectedBrandTerm';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { InsightBox } from '../InsightBox';
import { useStyles } from './useStyles';

const propTypes = {
  classes: PropTypes.shape({}),
  terms: PropTypes.arrayOf(
    PropTypes.shape({
      brandTerm: PropTypes.shape({}).isRequired,
      termInText: PropTypes.string.isRequired,
    })
  ).isRequired,
  onApplyDetectedBrandTerm: PropTypes.func.isRequired,
  onEditBrandTerm: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  insightsBoxBorder: PropTypes.bool,
  shrunk: PropTypes.bool,
  brandTermsBrandVoices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  brandTerms: PropTypes.arrayOf(PropTypes.shape({})),
  brandRulesEnabled: PropTypes.bool,
};

const defaultProps = {
  classes: null,
  onEditBrandTerm: null,
  loading: false,
  disabled: false,
  insightsBoxBorder: false,
  shrunk: false,
  brandTermsBrandVoices: [],
  brandTerms: [],
  brandRulesEnabled: false,
};

export const BrandTerms = ({
  classes,
  terms,
  onApplyDetectedBrandTerm,
  onEditBrandTerm,
  loading,
  disabled,
  insightsBoxBorder,
  shrunk,
  brandTermsBrandVoices,
  brandTerms,
  brandRulesEnabled,
}) => {
  const styles = useStyles({ classes });

  const prepareSubtitle = (brandVoiceId) => {
    if (!brandRulesEnabled) {
      return 'Brand rule enforcement is turned off';
    }

    if (terms.length === 0) {
      return 'No brand rules found';
    }

    const brandVoiceBrandTerms = terms?.filter(
      ({ brandTerm }) => brandTerm.brandVoice?.id === brandVoiceId
    );

    if (brandVoiceBrandTerms.length === 0) {
      return 'No brand rules found';
    }

    if (loading || brandVoiceBrandTerms?.length > 0) {
      return '';
    }

    if (brandTerms.filter((brandRule) => brandRule.brandVoiceId === brandVoiceId).length === 0) {
      return 'No brand rules defined yet';
    }
  };

  return (
    <div className={styles.root}>
      <CustomTypography variant={TYPOGRAPHY_VARIANT.medium12}>Brand rules</CustomTypography>
      <div className={cx(styles.wrapper, { [styles.border]: insightsBoxBorder })}>
        {brandTermsBrandVoices.map((brandVoice) => (
          <InsightBox
            key={brandVoice.id}
            className={styles.box}
            classes={{ collapseWrapperInner: styles.collapseWrapperInner }}
            title={brandVoice.name}
            subtitle={prepareSubtitle(brandVoice.id)}
            brandVoiceId={brandVoice.id}
          >
            {loading ? (
              <DetectedBrandTermSkeleton />
            ) : (
              <>
                {!brandRulesEnabled
                  ? null
                  : terms
                      ?.filter(({ brandTerm }) => brandTerm.brandVoice?.id === brandVoice.id)
                      .map(({ brandTerm, termInText }) => (
                        <DetectedBrandTerm
                          className={styles.detectedBrandTerm}
                          key={brandTerm.id}
                          brandTerm={brandTerm}
                          termInText={termInText}
                          onApplyClick={onApplyDetectedBrandTerm}
                          onEditClick={onEditBrandTerm}
                          disabled={disabled}
                          shrunk={shrunk}
                        />
                      ))}
              </>
            )}
          </InsightBox>
        ))}
      </div>
    </div>
  );
};

BrandTerms.propTypes = propTypes;
BrandTerms.defaultProps = defaultProps;
